import { FC, useEffect, useState } from "react";
import clsx from "classnames";
import { useTranslation } from "react-i18next";
import { InventoryAvatarData } from "@onmo/graphql/graphql";
import { Dialog } from "../../core/Dialog/Dialog";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import ArrowWrapper from "../ArrowWrapper/ArrowWrapper";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useFetchFreeAvatars } from "../../../hooks/useSelectAvatarQuery";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isGamepadMode } from "../../../hooks/useCrossInput";
import { isTV } from "../../../utils/platform";
import { getLocalizedField } from "../../../models/localizedField";
import { useStyles } from "./ChooseAvatarPopup.style";

interface IChooseAvatarPopupProps {
  open: boolean;
  selectedId?: string;
  onSave: (inventoryId: string, url: string) => void;
  onClose: () => void;
}

export const ChooseAvatarPopup: FC<IChooseAvatarPopupProps> = ({
  open,
  selectedId,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { avatarBundles, freeAvatars } = useFetchFreeAvatars();
  const { setActiveElement } = useGamepadContext();
  const [pendingId, setPendingId] = useState(selectedId);

  useEffect(() => setPendingId(selectedId), [selectedId, open]);

  // The highlight starts on the first avatar of the row
  const focusId = avatarBundles.find((bundle) => bundle.avatars.length)
    ?.avatars[0]?.id;

  useEffect(() => {
    if (!open || !focusId) return;
    const frame = requestAnimationFrame(() => {
      if (!isGamepadMode() && !isTV) {
        document
          .querySelectorAll<HTMLElement>('[data-testid="choose-avatar-list"]')
          .forEach((list) => (list.scrollLeft = 0));
        return;
      }
      const target = document.querySelector<HTMLElement>(
        'dialog [data-gamepad="default-selected"]',
      );
      if (!target || target === document.activeElement) return;
      setActiveElement(target);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focusId, setActiveElement]);

  const selectAvatar = (avatarId: string) => {
    setPendingId(avatarId);
    if (!isGamepadMode() && !isTV) return;
    requestAnimationFrame(() => {
      const save = document.querySelector<HTMLElement>(
        'dialog [data-gamepad="choose-avatar-save"]',
      );
      if (save) setActiveElement(save);
    });
  };

  const pending = freeAvatars.find((avatar) => avatar.id === pendingId);

  return (
    <Dialog
      open={open}
      data-dialog-nav="rows"
      rootClassName={classes.dialog}
      rootContentClassName={classes.dialogContent}
      title={t("ChooseAvatar_Choose Avatar")}
      onClose={onClose}
      mainAction={{
        text: t("ChooseAvatar_Save"),
        disabled: !pending,
        onClick: () =>
          pending &&
          onSave(pending.id, (pending.data as InventoryAvatarData)?.url),
        "data-gamepad": "choose-avatar-save",
      }}
      secondAction={{
        text: t("ChooseAvatar_Cancel"),
        onClick: onClose,
        "data-gamepad": "choose-avatar-cancel",
      }}
    >
      <div className={classes.content}>
        <Typography
          variant={TypographyVariant.HeadingH5Bold}
          className={classes.title}
        >
          {t("ChooseAvatar_Choose Avatar")}
        </Typography>
        <div className={classes.bundleList}>
          {avatarBundles.map((bundle) => {
            const bundleTitle =
              getLocalizedField(bundle.metadata, "title", i18n.language) ||
              bundle.title;
            return (
              <div key={bundle.id} className={classes.bundle}>
                <Typography
                  variant={TypographyVariant.BodyB4Bold}
                  className={classes.bundleTitle}
                >
                  {bundleTitle}
                </Typography>
                <ArrowWrapper
                  className={classes.avatarList}
                  size={20}
                  showWhenScrollable
                  role="radiogroup"
                  aria-label={bundleTitle}
                  data-testid="choose-avatar-list"
                >
                  {bundle.avatars.map((avatar, i) => (
                    <div
                      key={avatar.id}
                      className={clsx(classes.avatar, {
                        [classes.avatarActive]: avatar.id === pendingId,
                      })}
                      role="radio"
                      tabIndex={-1}
                      aria-checked={avatar.id === pendingId}
                      aria-label={t("A11y_Avatar number", { number: i + 1 })}
                      onClick={() => selectAvatar(avatar.id)}
                      data-gamepad={
                        avatar.id === focusId
                          ? "default-selected"
                          : `choose-avatar-${avatar.id}`
                      }
                      data-selected-class={classes.avatarSelected}
                    >
                      <StaticImage
                        alt=""
                        className={classes.avatarImage}
                        src={(avatar.data as InventoryAvatarData)?.url}
                      />
                    </div>
                  ))}
                </ArrowWrapper>
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
};
